/**
 * Session Manager Utility
 * Handles session expiry, cleanup, and redirects
 */

import logger from './logger';
import { sanitizeLogMessage, sanitizePathSegment } from './sanitize';

/**
 * Silently clear expired authentication cookies
 * Used when anonymous users have stale cookies
 */
export const clearExpiredCookies = () => {
  // Clear auth cookies
  const cookiesToClear = [
    'at',
    'as',
    'rt',
    'rtid',
    'ow-c',
    'XSRF-TOKEN',
  ];
  
  cookiesToClear.forEach(name => {
    // Clear for current domain
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    
    // Clear with domain attribute (for cross-subdomain cookies)
    const domain = window.location.hostname.split('.').slice(-2).join('.');
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.${domain};`;
  });
  
  // Clear localStorage client data
  localStorage.removeItem('c');
};

/**
 * Handle session expiry for authenticated users
 * Dispatches a custom event so ClientContext can show the session expiry modal
 * @param {string} tenanturlname - Optional tenant URL name for redirect
 */
export const handleSessionExpired = (tenanturlname = null) => {
  const currentTenant = tenanturlname ||
                        window.location.pathname.split('/')[1] ||
                        'osaka';

  // Dispatch custom event for ClientContext to show the modal, clean state,
  // broadcast to other tabs, and redirect in one place.
  window.dispatchEvent(new CustomEvent('session-expired', {
    detail: { tenantUrl: currentTenant },
  }));
};

/**
 * Check if user has authentication tokens
 * Used to determine if user was intentionally authenticated
 * @returns {boolean} True if user has auth tokens
 */
export const hasAuthTokens = () => {
  const cookies = document.cookie.split(';').reduce((acc, cookie) => {
    const [name, value] = cookie.trim().split('=');
    acc[name] = value;
    return acc;
  }, {});
  
  return !!(cookies.at || cookies.rt || cookies.rtid || cookies['ow-c']);
};

/**
 * Get tenant URL from current path or cookie
 * @returns {string} Tenant URL name
 */
export const getTenantUrlFromPath = () => {
  // Try to get from URL path first
  const pathSegments = window.location.pathname.split('/').filter(Boolean);
  if (pathSegments.length > 0 && !pathSegments[0].includes('.')) {
    return sanitizePathSegment(pathSegments[0]);
  }
  
  // Try to get from ow-c cookie
  try {
    const owCookie = document.cookie
      .split(';')
      .find(c => c.trim().startsWith('ow-c='));
    
    if (owCookie) {
      const value = owCookie.split('=')[1];
      const decoded = decodeURIComponent(value);
      // Handle both JSON string and direct object
      const data = decoded.startsWith('j:') 
        ? JSON.parse(decoded.substring(2))
        : JSON.parse(decoded);
      
      if (data.tenanturlname) {
        return data.tenanturlname;
      }
    }
  } catch (error) {
    logger.warn('SessionManager - Could not parse tenant from cookie:', sanitizeLogMessage(error));
  }
  
  // Default fallback
  return 'osaka';
};
